import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);   // null = checking, false = unauthenticated, object = user
    const [error, setError] = useState("");
    // Incrementing counter that any component (e.g. Sidebar) can subscribe to
    // when it needs to re-fetch derived data like /me/nav. Bumped any time an
    // admin changes a group's nav_keys so the sidebar reflects the change
    // without requiring a full page reload.
    const [navVersion, setNavVersion] = useState(0);
    const bumpNav = useCallback(() => setNavVersion((v) => v + 1), []);

    const refreshMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch (e) {
            setUser(false);
            return null;
        }
    }, []);

    useEffect(() => {
        refreshMe();
    }, [refreshMe]);

    const login = async (email, password) => {
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data?.access_token) localStorage.setItem("haevn_token", data.access_token);
            // Pull the full /auth/me to get groups/preview_groups
            await refreshMe();
            return true;
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
            return false;
        }
    };

    const register = async (email, password, name) => {
        setError("");
        try {
            const { data } = await api.post("/auth/register", { email, password, name });
            if (data?.access_token) localStorage.setItem("haevn_token", data.access_token);
            await refreshMe();
            return true;
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
            return false;
        }
    };

    const partnerSignup = async ({ email, password, name, company }) => {
        setError("");
        try {
            const { data } = await api.post("/auth/partner-signup", { email, password, name, company });
            if (data?.access_token) localStorage.setItem("haevn_token", data.access_token);
            await refreshMe();
            return true;
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
            return false;
        }
    };

    const requestMagicLink = async (email) => {
        setError("");
        try {
            await api.post("/auth/magic-link/request", { email });
            return true;
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
            return false;
        }
    };

    const consumeMagicLink = async (token) => {
        setError("");
        try {
            const { data } = await api.post("/auth/magic-link/consume", { token });
            if (data?.access_token) localStorage.setItem("haevn_token", data.access_token);
            await refreshMe();
            return true;
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
            return false;
        }
    };

    const changePassword = async (newPassword) => {
        setError("");
        try {
            await api.post("/auth/change-password", { new_password: newPassword });
            await refreshMe();
            return true;
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
            return false;
        }
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
        localStorage.removeItem("haevn_token");
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, login, register, partnerSignup, requestMagicLink, consumeMagicLink, changePassword, logout, refreshMe, error, setError, navVersion, bumpNav }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
